/**
 * Movement system: advances units along their path waypoints at fixed speed.
 * Pure position integration + facing. Path/intent setting happens in other
 * systems / the command executor. A light separation pass keeps units from
 * perfectly stacking (cheap grid hash, O(n) average).
 */
import type { World } from "../world";
import type { Registry } from "../../data/registry";
import { FIXED_DT } from "../types";
import { advanceOrder } from "../orders";
import { findPath, nearestWalkableHex, terrainSegmentClear } from "../pathfinding";
import { domainOf } from "../naval";
import {
  RAMPART_HALF_WIDTH_RATIO,
  rampartGeometry,
  rampartPush,
  rampartSegmentBlocked,
} from "../ramparts";

/** Soft-collision footprint of a resource prop (the trunk/boulder), world units.
 *  Small: units weave THROUGH a forest, they just can't overlap a trunk. The
 *  renderer reads this too, to draw the nav-debug wireframe. */
export function propRadius(defId: string, size: number): number {
  switch (defId) {
    case "stone_rock": return 0.18 * size;
    case "gold_ore": return 0.15 * size;
    case "berry_bush": return 0.11 * size;
    case "tree": return 0.09 * size; // thin trunk — units brush right past the canopy
    default: return 0.13 * size;
  }
}

/** Sub-tile collision radius of a BUILDING's model (world units). A building only
 *  fills this circle of its tile, NOT the whole tile — so a unit (its own radius
 *  added) squeezes between small buildings (house) but a big unit can't fit gaps
 *  flanked by big ones (town centre). Tuned to the low-poly models in makeBuilding;
 *  the tile half-width (centre→edge) is ~0.87·size, so a 0.26 house leaves a wide
 *  lane while a 0.55 town centre leaves only a sliver. Fields = 0 (walk on the crop). */
export function buildingRadius(defId: string, size: number): number {
  switch (defId) {
    case "town_center": return 0.55 * size;
    case "barracks": return 0.44 * size;
    case "temple": return 0.42 * size;
    case "stables": return 0.40 * size;
    case "storage_pit": return 0.26 * size;
    case "house": return 0.26 * size;
    case "field": return 0; // a worked field is walkable (farmers stand on the rows)
    // WALLS / GATES are SOLID (no sub-tile footprint) — this value is only their
    // CONTACT radius. Make it big so an attacker reaches the wall from the ADJACENT
    // tile (its model touching the rampart) instead of having to stand ON the solid
    // hex, where separation would shove it off and it'd ram-and-bounce forever.
    case "wall": return 0.7 * size;
    case "gate": return 0.65 * size;
    case "tower": return 0.44 * size;
    default: return 0.36 * size;
  }
}

export function movementSystem(world: World, _reg: Registry): void {
  // Resource props as SOFT obstacles, keyed by hex. Their tile stays passable
  // (pathing routes through forests), but a moving unit can't sit on the trunk —
  // it gets nudged to the edge, the same way units avoid each other. Fields and
  // meat carcasses (kind building / animal) are excluded — you can stand on those.
  const props = new Map<number, { eid: number; x: number; z: number; r: number }>();
  for (const eid of world.resource.keys()) {
    const id = world.ident.get(eid);
    if (id?.kind !== "resource") continue;
    const tr = world.transform.get(eid);
    if (!tr) continue;
    const h = world.hexAtWorld(tr.pos.x, tr.pos.y);
    // fish schools sit on open water and aren't physical obstacles — never repel
    // ships off them (the fishing boat needs to sit right on its catch).
    if (h && !world.isWater(h.col, h.row)) props.set(world.hkey(h.col, h.row), { eid, x: tr.pos.x, z: tr.pos.y, r: propRadius(id.defId, world.size) });
  }
  // LAND buildings are soft obstacles too: their tile is passable, but the model
  // circle repels units, so a unit routes around the building (squeezing between
  // small ones). Keyed by the building's hex; the attacker/builder of a given
  // building is exempted below so it can reach the wall.
  for (const [eid, b] of world.building) {
    const fp = world.propPos.get(world.hkey(b.origin.x, b.origin.y));
    if (fp && world.buildingHexKeys.has(world.hkey(b.origin.x, b.origin.y))) {
      props.set(world.hkey(b.origin.x, b.origin.y), { eid, x: fp.x, z: fp.z, r: fp.r });
    }
  }

  // WALLS are sub-tile MODELS — a thin rampart BAR, not a whole-tile block. A unit
  // stands on the free part of a wall's tile and walks beside a lone wall; only a
  // RUN of walls forms a continuous bar it can't cross. Collider per built wall: a
  // hub at its centre + a bar to each adjacent built wall (the rendered arms).
  const walls = rampartGeometry(world);
  const wallPadding = (radius: number) => radius;
  const teamOf = (player?: number) => player === undefined ? undefined : world.players.get(player)?.team;
  const wallPushAt = (px: number, py: number, radius: number, player?: number) =>
    rampartPush(walls, px, py, wallPadding(radius), teamOf(player));
  const wallSweepClear = (ax: number, ay: number, bx: number, by: number, radius: number, player?: number) =>
    !rampartSegmentBlocked(walls, ax, ay, bx, by, wallPadding(radius), teamOf(player));
  const admitImpulse = (eid: number, tr: { pos: { x: number; y: number }; radius: number }, nx: number, ny: number): boolean => {
    const h = world.hexAtWorld(nx, ny);
    return !!h &&
      world.passableFor(domainOf(world, eid), h.col, h.row, world.owner.get(eid)?.player) &&
      terrainSegmentClear(world, tr.pos.x, tr.pos.y, nx, ny, domainOf(world, eid), world.owner.get(eid)?.player) &&
      wallPushAt(nx, ny, tr.radius, world.owner.get(eid)?.player) === null &&
      wallSweepClear(tr.pos.x, tr.pos.y, nx, ny, tr.radius, world.owner.get(eid)?.player);
  };

  // A gather target is allowed as the interaction object. Building interactions
  // are deliberately NOT exempt: their path already ends at a model-clear contact
  // point, and exempting the target here would let separation/steering push a body
  // through the building texture.
  const exemptOf = (id: number): number => {
    const gc = world.gatherer.get(id);
    if (gc?.node != null && gc.node >= 0 && world.resource.has(gc.node)) return gc.node;
    return -1;
  };

  // advance along paths
  for (const [eid, mv] of world.move) {
    const tr = world.transform.get(eid);
    if (!tr) continue;
    const node = exemptOf(eid); // the prop/building this unit is allowed to reach
    const uDom = domainOf(world, eid);
    const uPlayer = world.owner.get(eid)?.player;
    const canStand = (px: number, py: number): boolean => {
      const hh = world.hexAtWorld(px, py);
      if (!hh || !world.passableFor(uDom, hh.col, hh.row, uPlayer)) return false;
      return wallPushAt(px, py, tr.radius, uPlayer) === null; // not inside a rampart bar
    };
    const canMove = (px: number, py: number): boolean =>
      canStand(px, py) &&
      terrainSegmentClear(world, tr.pos.x, tr.pos.y, px, py, uDom, uPlayer) &&
      wallSweepClear(tr.pos.x, tr.pos.y, px, py, tr.radius, uPlayer);
    let blocked = false;
    let remaining = mv.speed * FIXED_DT;
    while (remaining > 0 && mv.path.length > 0) {
      const wp = mv.path[0];
      const dx = wp.x - tr.pos.x;
      const dy = wp.y - tr.pos.y;
      const d = Math.hypot(dx, dy);
      // A waypoint is "reached" once the unit is near it — NOT pixel-exact: the
      // veer/repel steering constantly nudges a unit off the precise point, so a
      // 0-tolerance target makes it ORBIT a waypoint forever without ever popping
      // it (the stuck-near-an-obstacle bug). A waypoint sitting ON a prop gets the
      // larger prop-radius tolerance (it can't get closer than the repel allows).
      const wh = world.hexAtWorld(wp.x, wp.y);
      const wpProp = wh ? props.get(world.hkey(wh.col, wh.row)) : undefined;
      // Inflate the arrival tolerance only for an obstacle the unit must skirt — NOT
      // for its EXEMPT target (the drop-site/build site it's walking up to). A big
      // building's footprint would otherwise mark the unit "arrived" a whole tile
      // short, outside the deposit/build radius, freezing it there.
      // A continuous waypoint may share a visual tile with a prop while sitting
      // safely beside its actual footprint. Only inflate tolerance when the
      // waypoint itself is inside the obstacle; tile membership alone would make
      // model-edge interaction paths stop half a tile early.
      const waypointInsideProp = wpProp && wpProp.eid !== node &&
        Math.hypot(wp.x - wpProp.x, wp.y - wpProp.z) < wpProp.r + tr.radius;
      const pr = waypointInsideProp ? wpProp.r : undefined;
      const baseTol = world.size * 0.08;
      const tol = pr !== undefined ? pr + tr.radius + world.size * 0.06 : baseTol;
      if (d <= tol) {
        mv.path.shift();
        continue;
      }
      // STEER toward the waypoint while veering around nearby props — smooth
      // obstacle avoidance in continuous space, so a unit curves cleanly around a
      // trunk/bush (using the open room around it) instead of walking into it.
      let sx = dx / d, sy = dy / d;
      const uh = world.hexAtWorld(tr.pos.x, tr.pos.y);
      if (uh) {
        const veer = (hk: number) => {
          const p = props.get(hk);
          if (!p || p.eid === node) return;
          const ax = tr.pos.x - p.x, az = tr.pos.y - p.z;
          const ad = Math.hypot(ax, az);
          const R = p.r + tr.radius + world.size * 0.55; // start veering early
          if (ad > 1e-3 && ad < R) {
            const k = (R - ad) / R; // closer ⇒ stronger
            // tangential bias: slide AROUND the prop (perpendicular to approach)
            // rather than only backing off, so a prop dead-ahead doesn't stall it
            const tnx = -dy / d, tny = dx / d;
            const side = (ax * tnx + az * tny) >= 0 ? 1 : -1;
            sx += ((ax / ad) * 0.7 + tnx * side) * k * 1.2;
            sy += ((az / ad) * 0.7 + tny * side) * k * 1.2;
          }
        };
        veer(world.hkey(uh.col, uh.row));
        for (const [nc, nr] of world.neighbors(uh.col, uh.row)) veer(world.hkey(nc, nr));
        const sl = Math.hypot(sx, sy);
        if (sl > 1e-6) { sx /= sl; sy /= sl; }
      }
      const step = Math.min(remaining, d);
      let nx = tr.pos.x + sx * step, ny = tr.pos.y + sy * step;
      // Never walk THROUGH a solid (a wall/gate, or a building placed on the route
      // AFTER this path was computed) or off the movement domain — slide ALONG it
      // (so a crowd flows past instead of bouncing), or stop if fully cornered.
      if (!canMove(nx, ny)) {
        // Slide ALONG the obstacle — but ONLY if the slide actually moves the unit on
        // that axis. A degenerate "slide" back to the CURRENT position (the goal sits
        // dead ahead through a wall) makes zero progress yet looks valid, so the unit
        // spins in place forever and never re-routes. Require real motion, else break
        // → the re-route below finds the way AROUND.
        if (canMove(nx, tr.pos.y) && Math.abs(nx - tr.pos.x) > 1e-6) ny = tr.pos.y;
        else if (canMove(tr.pos.x, ny) && Math.abs(ny - tr.pos.y) > 1e-6) nx = tr.pos.x;
        else { blocked = true; break; }
      }
      tr.pos.x = nx;
      tr.pos.y = ny;
      tr.facing = Math.atan2(sy, sx);
      remaining -= step;
      if (Math.hypot(wp.x - tr.pos.x, wp.y - tr.pos.y) <= tol) {
        mv.path.shift();
      }
    }
    // Cornered against a solid we couldn't slide past — the route was invalidated
    // (a building went up on it). Re-route to the goal around it (null ⇒ give up).
    if (blocked && mv.path.length > 0) {
      const goal = mv.path[mv.path.length - 1];
      mv.path = findPath(world, tr.pos.x, tr.pos.y, goal.x, goal.y, 6000, uDom, uPlayer, false, tr.radius) ?? [];
    }
    // arrival: a move/attackMove order is complete — pull the next queued order,
    // or go idle (undefined) when none remain.
    if (mv.path.length === 0 && (mv.intent?.kind === "move" || mv.intent?.kind === "attackMove")) {
      if (!advanceOrder(world, eid)) { mv.intent = undefined; mv.guard = { ...tr.pos }; }
    }
  }

  // light separation (units only) — keep units from perfectly stacking, but
  // let them CROWD like a normal RTS. The separation distance is scaled to the
  // hex size (units are ~1/3 of a hex), not the full collision/combat radius —
  // otherwise units kept a full hex apart and "bounced" instead of packing.
  const sepR = world.size * 0.18;
  const sepMinD = sepR * 2;
  const cell = Math.max(0.5, sepMinD);
  const grid = new Map<number, number[]>();
  for (const [eid, tr] of world.transform) {
    if (!world.move.has(eid)) continue; // units only
    const key =
      (Math.floor(tr.pos.y / cell) & 0xffff) * 65536 +
      (Math.floor(tr.pos.x / cell) & 0xffff);
    let arr = grid.get(key);
    if (!arr) {
      arr = [];
      grid.set(key, arr);
    }
    arr.push(eid);
  }
  const push = 0.12;
  // A worker actively gathering is "anchored" on its resource tile — separation
  // must not shove it off, or it ends up harvesting from a tile away.
  const anchored = (id: number): boolean => world.gatherer.get(id)?.state === "gathering";
  // a separation nudge that REFUSES to cross the entity's movement domain — so a
  // crowded ship is never shoved onto land (nor a land unit onto water).
  const nudge = (id: number, t: { pos: { x: number; y: number }; radius: number }, ddx: number, ddy: number): void => {
    const nx = t.pos.x + ddx, ny = t.pos.y + ddy;
    if (admitImpulse(id, t, nx, ny)) { t.pos.x = nx; t.pos.y = ny; }
  };
  for (const [eid, tr] of world.transform) {
    if (!world.move.has(eid)) continue;
    const cx = Math.floor(tr.pos.x / cell);
    const cy = Math.floor(tr.pos.y / cell);
    for (let oy = -1; oy <= 1; oy++) {
      for (let ox = -1; ox <= 1; ox++) {
        const key = ((cy + oy) & 0xffff) * 65536 + ((cx + ox) & 0xffff);
        const arr = grid.get(key);
        if (!arr) continue;
        for (const other of arr) {
          if (other <= eid) continue;
          const oTr = world.transform.get(other);
          if (!oTr) continue;
          const dx = tr.pos.x - oTr.pos.x;
          const dy = tr.pos.y - oTr.pos.y;
          const d = Math.hypot(dx, dy);
          if (d > 0 && d < sepMinD) {
            const overlap = (sepMinD - d) / 2;
            const nx = dx / d;
            const ny = dy / d;
            // domain-clamped so a crowded ship is never pushed off the water (and a
            // shore-packed land unit never onto the sea) — beaching/drowning bug.
            if (!anchored(eid)) nudge(eid, tr, nx * overlap * push, ny * overlap * push);
            if (!anchored(other)) nudge(other, oTr, -nx * overlap * push, -ny * overlap * push);
          }
        }
      }
    }
  }

  // push units/animals out of resource props (static, so push FULLY clear). The
  // one prop a gatherer is actively working is exempt, so a worker can stand at
  // its tree/bush to harvest.
  if (props.size > 0) {
    for (const [eid, tr] of world.transform) {
      if (!world.move.has(eid)) continue;
      const h = world.hexAtWorld(tr.pos.x, tr.pos.y);
      if (!h) continue;
      const myR = tr.radius;
      const node = exemptOf(eid);
      const repel = (hk: number) => {
        const p = props.get(hk);
        if (!p || p.eid === node) return;
        const dx = tr.pos.x - p.x, dz = tr.pos.y - p.z;
        const d = Math.hypot(dx, dz);
        const minD = p.r + myR;
        let nx = tr.pos.x, ny = tr.pos.y;
        if (d > 1e-4 && d < minD) {
          // gentle, damped nudge (like unit-vs-unit separation) so a unit can
          // still push PAST a trunk it's pathing around instead of being teleported
          // back as fast as it advances.
          const k = ((minD - d) * 0.35) / d;
          nx = tr.pos.x + dx * k;
          ny = tr.pos.y + dz * k;
        } else if (d <= 1e-4) {
          nx = tr.pos.x + minD; // sitting dead-centre → shove out deterministically
        } else return;
        // DOMAIN CLAMP: never shove a unit off its movement domain — a coastal
        // crowd must not push a land unit into the SEA (nor a ship onto land). Same
        // guard the separation nudge uses; without it units appeared to "walk" on water.
        if (admitImpulse(eid, tr, nx, ny)) {
          tr.pos.x = nx; tr.pos.y = ny;
        }
      };
      repel(world.hkey(h.col, h.row));
      for (const [nc, nr] of world.neighbors(h.col, h.row)) repel(world.hkey(nc, nr));
    }
  }

  // WALL BAR repel: shove anyone whose body overlaps a rampart bar (a wall just
  // finished under them, or a crowd nudged them in) out to the nearest free space —
  // gently, staying on the tile if it fits, and NEVER across their movement domain.
  // This REPLACES the old whole-tile eject: a unit keeps the free part of a wall's
  // tile instead of being teleported off it.
  if (walls.hubs.length > 0 || walls.arms.length > 0) {
    for (const [eid, tr] of world.transform) {
      if (!world.move.has(eid)) continue;
      const push = wallPushAt(tr.pos.x, tr.pos.y, tr.radius, world.owner.get(eid)?.player);
      if (!push) continue;
      const candidates: { x: number; y: number }[] = [
        { x: tr.pos.x + push.x, y: tr.pos.y + push.y },
      ];
      // At a corner/T-junction, the nearest-feature push can end inside another
      // arm. Recovery starts from an already-invalid point, so a normal sweep is
      // impossible; sample a deterministic ring and choose the first fully clear
      // point instead of letting successive pushes flip the body through the wall.
      const escapeDistance = tr.radius + world.size * (RAMPART_HALF_WIDTH_RATIO + 0.01);
      for (let i = 0; i < 16; i++) {
        const angle = (i * Math.PI * 2) / 16;
        candidates.push({
          x: tr.pos.x + Math.cos(angle) * escapeDistance,
          y: tr.pos.y + Math.sin(angle) * escapeDistance,
        });
      }
      for (const candidate of candidates) {
        const nh = world.hexAtWorld(candidate.x, candidate.y);
        const dom = domainOf(world, eid);
        const player = world.owner.get(eid)?.player;
        if (!nh || !world.passableFor(dom, nh.col, nh.row, player)) continue;
        if (!terrainSegmentClear(world, tr.pos.x, tr.pos.y, candidate.x, candidate.y, dom, player)) continue;
        if (wallPushAt(candidate.x, candidate.y, tr.radius, player) !== null) continue;
        tr.pos.x = candidate.x;
        tr.pos.y = candidate.y;
        break;
      }
    }
  }

  // SAFETY NET: no unit may END a tick OFF its movement domain. Steering/veer around
  // a shoreline building can edge a land unit over the waterline (its position is
  // integrated continuously and not every write is domain-clamped). Catch any such
  // unit and pull it back toward the nearest in-domain hex by a capped step — gentle
  // (no teleport), and a no-op for the >99% of units already on valid ground.
  for (const [eid, tr] of world.transform) {
    if (!world.move.has(eid)) continue;
    const h = world.hexAtWorld(tr.pos.x, tr.pos.y);
    if (!h) continue;
    const dom = domainOf(world, eid);
    const player = world.owner.get(eid)?.player;
    // ONLY rescue a true terrain-domain violation — a LAND unit in the water (or a
    // ship beached). Crucially NOT a unit standing on a wall/building hex (it may be
    // attacking it), or this would yank attackers off and cause ram-and-bounce jitter.
    const offTerrain = dom === "land" ? world.isWater(h.col, h.row) : !world.isWater(h.col, h.row);
    if (!offTerrain) continue;
    const back = nearestWalkableHex(world, h.col, h.row, 4, tr.pos.x, tr.pos.y, dom, player);
    if (!back) continue;
    const c = world.hexCenter(back[0], back[1]);
    const dx = c.x - tr.pos.x, dy = c.y - tr.pos.y;
    const d = Math.hypot(dx, dy) || 1;
    const step = Math.min(d, world.size * 0.6);
    const nx = tr.pos.x + (dx / d) * step;
    const ny = tr.pos.y + (dy / d) * step;
    // Recovery deliberately begins outside the entity's valid terrain domain, so
    // the normal terrain sweep cannot admit it. It must still obey every physical
    // rampart sweep: an invalid placement must not become a wall/gate teleport.
    if (!wallSweepClear(tr.pos.x, tr.pos.y, nx, ny, tr.radius, player) ||
        wallPushAt(nx, ny, tr.radius, player) !== null) continue;
    tr.pos.x = nx;
    tr.pos.y = ny;
  }
}
